import { motion } from 'framer-motion';

// Cinematic claim-pulse overlay. Triggered by ProgressionScreen
// when a reward is claimed, dismissed automatically after ~1.1s.
//
// Composed of seven layers stacked so the ripple reads as "magical
// energy radiating out from the relic the player just collected"
// rather than a generic UI animation:
//
//   1. Initial flash. Bright cream-gold disc igniting at origin
//   2. Primary shockwave. Elliptical gold ring sweeping outward at
//      canvas aspect ratio so it sweeps edge-to-edge in proportion
//      (no circular squash on the 16:9 canvas)
//   3. Secondary shockwave. Dimmer follow-up ring, delayed
//   4. Tertiary bloom. Slowest, widest, most diffuse ring (lingers)
//   5. Radial tint. Screen-wide cream-gold tint emanating from
//      origin, peaks at ~150ms then fades. "the world brightens"
//   6. Vignette pulse. Subtle edge darkening that closes then
//      reopens. Atmospheric pressure as the wave crosses you
//   7. Spark particles. 12 gold motes radiating in a fan, with
//      slight upward drift gravity for ember feel
//
// Coordinates (x, y) are in canvas-relative percentages (cqw/cqh
// units). The wrapper element is `position: fixed`, which inside
// AppStage's `.app-canvas` anchors to the canvas rather than the
// viewport (because the canvas has `transform: translateZ(0)`
// creating a containing block). That lets the elliptical waves use
// 100cqw × 100cqh to perfectly match canvas dimensions.

// Soft, organic auras instead of hard rings. Each "wave" is a
// large elliptical radial gradient that grows from the click point
// outward. Bright glowing center, soft falloff into nothing, no
// geometric border anywhere. Heavy CSS blur stacks on top to push
// the edges further into "diffuse energy" territory, and
// mix-blend-mode: screen makes the auras add light rather than
// paint over the canvas (which would flatten the medieval texture
// beneath).
//
// Three layers staggered in time + space give the impression of a
// single billowing wave with depth, not three separate effects.
// Opacity ramps up to a peak mid-life then fades. The "warmth
// passing through you" feel. Rather than starting bright and just
// fading, which reads as static.
const SHOCKWAVES = [
  {
    delay: 0.00,
    fromScale: 0.08,
    toScale: 1.45,
    duration: 0.95,
    peakOpacity: 0.95,
    background:
      'radial-gradient(circle, rgba(255, 250, 220, 0.85) 0%, rgba(255, 226, 142, 0.55) 28%, rgba(232, 196, 102, 0.25) 50%, rgba(180, 140, 60, 0.08) 68%, transparent 82%)',
    blur: '1.3cqw',
    ease: [0.18, 0.72, 0.3, 1],
  },
  {
    delay: 0.14,
    fromScale: 0.12,
    toScale: 1.7,
    duration: 1.1,
    peakOpacity: 0.7,
    background:
      'radial-gradient(circle, rgba(255, 235, 175, 0.6) 0%, rgba(232, 196, 102, 0.35) 35%, rgba(180, 140, 60, 0.15) 58%, transparent 80%)',
    blur: '2.2cqw',
    ease: [0.2, 0.7, 0.32, 1],
  },
  {
    delay: 0.32,
    fromScale: 0.18,
    toScale: 2.0,
    duration: 1.25,
    peakOpacity: 0.5,
    background:
      'radial-gradient(circle, rgba(232, 196, 102, 0.45) 0%, rgba(180, 140, 60, 0.22) 45%, rgba(120, 90, 40, 0.08) 65%, transparent 82%)',
    blur: '3cqw',
    ease: [0.22, 0.7, 0.34, 1],
  },
];

const PARTICLE_COUNT = 14;

// Pseudo-random fan of spark trajectories. Generated at module
// load so every pulse uses the same set. Keeps the visual signature
// consistent without re-rolling angles each time. The slight upward
// bias on `dy` (negative is up) gives an ember-like rising drift.
const PARTICLES = Array.from({ length: PARTICLE_COUNT }, (_, i) => {
  const angle = (i / PARTICLE_COUNT) * Math.PI * 2 + (i % 2 === 0 ? 0.18 : -0.12);
  const reach = 18 + (i * 7) % 12;            // cqw units
  const liftBias = -2.5 - ((i * 3) % 5) * 0.4; // adds upward drift
  const dx = Math.cos(angle) * reach;
  const dy = Math.sin(angle) * reach + liftBias;
  return {
    dx,
    dy,
    delay: (i % 5) * 0.04,
    duration: 0.85 + ((i * 0.07) % 0.35),
    size: 0.22 + ((i * 0.05) % 0.18),         // cqw
  };
});

export default function ClaimPulse({ x, y }) {
  const left = `${x}cqw`;
  const top = `${y}cqh`;

  return (
    <div
      aria-hidden
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        // Sits BELOW the scrollable content (z-10) so the cell the
        // player just tapped stays crisp on top of the wave. The
        // mix-blend-mode: screen on the layers below additively
        // brightens whatever's underneath them on the canvas, which
        // happens to be the screen background + texture wash. The
        // cells themselves render on top in their own stacking
        // context, untouched by the blend.
        zIndex: 1,
        overflow: 'hidden',
      }}
    >
      {/* ── Layer 5: radial screen tint ────────────────────────── */}
      {/* Bright at origin, fading toward screen edges. Reads as
          "the world warms." Quick crest then quick decay. */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 0.35, 0] }}
        transition={{ duration: 0.55, times: [0, 0.22, 1], ease: 'easeOut' }}
        style={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(ellipse 75% 75% at ${left} ${top}, rgba(255, 235, 175, 1) 0%, rgba(232, 196, 102, 0.5) 28%, rgba(180, 140, 60, 0) 60%)`,
          mixBlendMode: 'screen',
        }}
      />

      {/* ── Layer 6: atmospheric vignette pulse ────────────────── */}
      {/* Subtle outer darkening that closes inward then releases.
          Sells the idea that the wave is passing THROUGH the screen,
          briefly squeezing the world. */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 0.45, 0] }}
        transition={{ duration: 0.85, times: [0, 0.5, 1], ease: 'easeInOut' }}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(ellipse 90% 80% at 50% 50%, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 55%, rgba(0,0,0,0.7) 100%)',
        }}
      />

      {/* ── Layer 1: initial flash ─────────────────────────────── */}
      {/* A bright cream-gold disc igniting at the click point.
          anchors the eye to the source of the wave. */}
      <motion.div
        initial={{ scale: 0.35, opacity: 1 }}
        animate={{ scale: 3.2, opacity: 0 }}
        transition={{ duration: 0.42, ease: [0.18, 0.8, 0.3, 1] }}
        style={{
          position: 'absolute',
          left,
          top,
          width: '4cqw',
          height: '4cqw',
          marginLeft: '-2cqw',
          marginTop: '-2cqw',
          borderRadius: '50%',
          background: 'radial-gradient(circle, #fffaeb 0%, #fbe9b8 30%, #e8c466 60%, rgba(232, 196, 102, 0) 100%)',
          filter: 'blur(0.3cqw)',
        }}
      />

      {/* ── Layers 2–4: organic aspect-correct auras ───────────── */}
      {/* Width 100cqw × height 100cqh → ellipse matches canvas
          aspect ratio (16:9). Each layer is a soft radial gradient
          (NO border, NO box-shadow) plus a CSS blur for diffuse
          edges, additively blended onto the screen. Scaling expands
          the aura outward; opacity peaks mid-life so the wave
          "swells through" the player. */}
      {SHOCKWAVES.map((w, i) => (
        <motion.div
          key={i}
          initial={{ scale: w.fromScale, opacity: 0 }}
          animate={{ scale: w.toScale, opacity: [0, w.peakOpacity, 0] }}
          transition={{
            delay: w.delay,
            duration: w.duration,
            ease: w.ease,
            times: [0, 0.38, 1],
          }}
          style={{
            position: 'absolute',
            left,
            top,
            width: '100cqw',
            height: '100cqh',
            marginLeft: '-50cqw',
            marginTop: '-50cqh',
            borderRadius: '50%',
            background: w.background,
            filter: `blur(${w.blur})`,
            mixBlendMode: 'screen',
            willChange: 'transform, opacity',
          }}
        />
      ))}

      {/* ── Layer 7: spark particles ───────────────────────────── */}
      {/* Each particle drifts outward + slightly up (ember gravity)
          with a soft gold glow. Distributed in a fan around origin
          with a stable pseudo-random pattern so the effect feels
          orchestrated, not chaotic. */}
      {PARTICLES.map((p, i) => (
        <motion.div
          key={`p-${i}`}
          initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
          animate={{
            x: `${p.dx}cqw`,
            y: `${p.dy}cqh`,
            opacity: 0,
            scale: 0.4,
          }}
          transition={{ delay: p.delay, duration: p.duration, ease: [0.2, 0.7, 0.35, 1] }}
          style={{
            position: 'absolute',
            left,
            top,
            width: `${p.size}cqw`,
            height: `${p.size}cqw`,
            marginLeft: `-${p.size / 2}cqw`,
            marginTop: `-${p.size / 2}cqw`,
            borderRadius: '50%',
            background: 'radial-gradient(circle, #fffaeb 0%, #fbe9b8 45%, rgba(232, 196, 102, 0) 100%)',
            boxShadow: '0 0 6px 1px rgba(255, 226, 142, 0.7)',
          }}
        />
      ))}
    </div>
  );
}
